<template>
  <!-- A product scene on the feature stage backdrop (dot grid and plan glow, as StoryChatStage).
       With `autoplay`, the scene inside plays once when it scrolls into view: every animation in
       it is written under `.is-run` (paused under `.is-armed`), so the server, no-JS and
       reduce-motion render the resting final frame. A Replay button appears after `duration`
       (keep it under 10 s, WCAG 2.2.2) and restarts the animations. Without `autoplay`, the
       child is driven by its own props (the sticky chapters). -->
  <div ref="rootEl" class="sst" :class="{ 'sst--tall': tall }">
    <div :key="runKey" class="sst-frame" :class="{ 'is-run': running || armed, 'is-armed': armed }">
      <slot />
    </div>
    <p v-if="label" class="sst-sr">{{ label }}</p>
    <button v-show="canReplay" type="button" class="sst-replay" :aria-label="replayLabel" :title="replayLabel" @click="replay">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" /></svg>
    </button>
  </div>
</template>

<script setup>
const props = defineProps({
  autoplay: { type: Boolean, default: true },
  // How long the scene plays (ms) before Replay shows.
  duration: { type: Number, default: 6000 },
  tall: { type: Boolean, default: false },
  // A plain-language description of the scene for screen readers (the replica is aria-hidden).
  label: { type: String, default: "" },
  replayLabel: { type: String, default: "Replay preview" },
});

const rootEl = ref(null);
const running = ref(false);
const armed = ref(false);
const canReplay = ref(false);
const runKey = ref(0);
let observer = null;
let timer = null;

const play = () => {
  armed.value = false;
  running.value = true;
  canReplay.value = false;
  clearTimeout(timer);
  timer = setTimeout(() => { canReplay.value = true; }, props.duration);
};
const replay = () => {
  runKey.value += 1;
  play();
};

onMounted(() => {
  if (!props.autoplay) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  armed.value = true;
  observer = new IntersectionObserver(([entry]) => {
    if (!entry.isIntersecting) return;
    observer.disconnect();
    play();
  }, { threshold: 0.35 });
  observer.observe(rootEl.value);
});

onBeforeUnmount(() => {
  clearTimeout(timer);
  observer?.disconnect();
});
</script>

<style>
.sst {
  --sst-p1: 124 155 224;
  --sst-p2: 159 139 230;
  --sst-dot: rgb(0 0 0 / 0.1);
  position: relative;
  height: 470px;
  border-radius: 14px;
  background-color: rgb(0 0 0 / 0.018);
  isolation: isolate;
}
.sst--tall { height: 520px; }
.sst::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  background:
    radial-gradient(circle, var(--sst-dot) 0.8px, transparent 1.3px) 7px 7px / 14px 14px,
    radial-gradient(150% 150% at 50% 45%, rgb(var(--sst-p1) / 0.16) 0%, rgb(var(--sst-p2) / 0.08) 50%, rgb(var(--sst-p2) / 0) 100%);
  -webkit-mask-image: radial-gradient(closest-side, #000 70%, rgb(0 0 0 / 0.55) 100%);
  mask-image: radial-gradient(closest-side, #000 70%, rgb(0 0 0 / 0.55) 100%);
}
.dark .sst { --sst-dot: rgb(255 255 255 / 0.13); background-color: rgb(255 255 255 / 0.03); }
.sst-frame { height: 100%; max-width: 520px; margin: 0 auto; padding: 28px 28px 30px; }
/* The feature pages (FeatureDetailPage, workspace-voice) show story frames at 540px, as StoryStage
   does; elsewhere they stay 520. Set by class, not by which stylesheet loads last. */
.sst-wide .sst-frame { max-width: 540px; }
.sst-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
.sst-replay {
  position: absolute;
  right: 8px;
  bottom: 4px;
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #595f68;
  cursor: pointer;
}
.dark .sst-replay { color: #a3a3a3; }
.sst-replay:hover { background: rgb(0 0 0 / 0.08); }
.sst-replay:focus-visible { outline: 2px solid #0e7490; outline-offset: 1px; }
.sst-replay svg { width: 20px; height: 20px; fill: currentColor; }
.sst-frame.is-armed * { animation-play-state: paused !important; }
@media (max-width: 639px) {
  .sst, .sst--tall { height: 500px; }
  .sst-frame { padding: 14px 12px 44px; }
}
@media (prefers-reduced-motion: reduce) {
  .sst-frame * { animation: none !important; transition: none !important; }
}
</style>
